import React, { useState, useEffect } from "react";
import api from "../services/api";

const inrFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0
});

export default function PickupRequests() {
  const [activeTab, setActiveTab] = useState("overview"); // overview | pickup_requests | customer_pickup | vendor_delivery | pickup_locations
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [overview, setOverview] = useState({
    pendingPickupRequests: 0,
    readyForPickup: 0,
    vendorDeliveries: 0,
    activePickupLocations: 0
  });

  const [orders, setOrders] = useState([]);
  const [pickupRequests, setPickupRequests] = useState([]);
  const [locations, setLocations] = useState([]);

  // Modals
  const [pickupModalOpen, setPickupModalOpen] = useState(false);
  const [selectedOrderForPickup, setSelectedOrderForPickup] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState("");
  const [pickupNotes, setPickupNotes] = useState("");

  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [newLocationForm, setNewLocationForm] = useState({
    name: "",
    street: "",
    addressLine2: "",
    city: "",
    state: "",
    pincode: "",
    landmark: "",
    contactPerson: "",
    contactPhone: "",
    operatingHours: "Mon - Sat: 10:00 AM - 8:00 PM",
    reason: ""
  });

  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [selectedOrderForDelivery, setSelectedOrderForDelivery] = useState(null);
  const [deliveryForm, setDeliveryForm] = useState({
    deliveryPersonName: "",
    deliveryPersonPhone: "",
    vehicleNumber: "",
    notes: ""
  });

  const [feedback, setFeedback] = useState({ type: "", msg: "" });

  const fetchData = async () => {
    try {
      setLoading(true);
      const [ovRes, ordRes, pkrRes, locRes] = await Promise.all([
        api.get("/vendor/fulfillment/overview"),
        api.get("/vendor/fulfillment/orders"),
        api.get("/vendor/fulfillment/pickup-requests"),
        api.get("/vendor/fulfillment/locations")
      ]);

      if (ovRes.data?.overview) setOverview(ovRes.data.overview);
      if (ordRes.data?.orders) setOrders(ordRes.data.orders);
      if (pkrRes.data?.pickupRequests) setPickupRequests(pkrRes.data.pickupRequests);
      if (locRes.data?.locations) setLocations(locRes.data.locations);
    } catch (err) {
      setFeedback({ type: "error", msg: err.response?.data?.msg || "Failed to load fulfillment data." });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Handle Request Pickup Submit
  const handleCreatePickupRequest = async (e) => {
    e.preventDefault();
    if (!selectedOrderForPickup || !selectedLocationId) {
      return alert("Please select an approved pickup location.");
    }

    try {
      setActionLoading(true);
      const res = await api.post("/vendor/fulfillment/pickup-request", {
        orderId: selectedOrderForPickup._id,
        pickupLocationId: selectedLocationId,
        notes: pickupNotes
      });

      setFeedback({ type: "success", msg: res.data?.message || "Pickup request submitted for Admin review." });
      setPickupModalOpen(false);
      setSelectedOrderForPickup(null);
      setSelectedLocationId("");
      setPickupNotes("");
      fetchData();
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to submit pickup request.");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Request New Pickup Location Submit
  const handleCreateLocationRequest = async (e) => {
    e.preventDefault();
    if (!newLocationForm.name || !newLocationForm.street || !newLocationForm.city || !newLocationForm.pincode || !newLocationForm.contactPerson || !newLocationForm.contactPhone) {
      return alert("Please fill in all required location fields.");
    }

    try {
      setActionLoading(true);
      const res = await api.post("/vendor/fulfillment/locations", newLocationForm);
      setFeedback({ type: "success", msg: res.data?.message || "Pickup location requested. Awaiting Admin approval." });
      setLocationModalOpen(false);
      setNewLocationForm({
        name: "",
        street: "",
        addressLine2: "",
        city: "",
        state: "",
        pincode: "",
        landmark: "",
        contactPerson: "",
        contactPhone: "",
        operatingHours: "Mon - Sat: 10:00 AM - 8:00 PM",
        reason: ""
      });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to request pickup location.");
    } finally {
      setActionLoading(false);
    }
  };

  // Handle Order Status Transitions
  const handleUpdateOrderStatus = async (orderId, targetStatus, deliveryInfo = {}) => {
    try {
      setActionLoading(true);
      const res = await api.put(`/vendor/fulfillment/orders/${orderId}/status`, {
        targetStatus,
        deliveryInfo
      });

      setFeedback({ type: "success", msg: res.data?.message || "Order updated successfully." });
      setDeliveryModalOpen(false);
      setSelectedOrderForDelivery(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.msg || "Failed to update order status.");
    } finally {
      setActionLoading(false);
    }
  };

  const approvedLocations = locations.filter(l => l.status === "APPROVED" && l.isActive);

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <div className="spinner-border text-brand-primary" role="status" />
        <p style={{ marginTop: "12px", color: "var(--text-muted)", fontSize: "14px" }}>Loading fulfillment dashboard...</p>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "1200px", margin: "0 auto", paddingBottom: "40px" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px", marginBottom: "20px" }}>
        <div>
          <h1 style={{ fontSize: "24px", fontWeight: "900", color: "#0f172a", margin: "0 0 4px" }}>
            🚚 Order Pickup &amp; Fulfillment Management
          </h1>
          <p style={{ fontSize: "14px", color: "var(--text-muted)", margin: 0 }}>
            Manage order pickups, vendor direct deliveries, customer counter pickups, and multi-location warehouses.
          </p>
        </div>

        <button
          type="button"
          onClick={() => setLocationModalOpen(true)}
          className="btn btn-primary"
          style={{ padding: "9px 18px", fontSize: "13px", fontWeight: "800", gap: "6px" }}
        >
          <span>+ Request New Pickup Location</span>
        </button>
      </div>

      {/* Feedback Banner */}
      {feedback.msg && (
        <div
          style={{
            background: feedback.type === "success" ? "#f0fdf4" : "#fef2f2",
            border: `1.5px solid ${feedback.type === "success" ? "#bbf7d0" : "#fecaca"}`,
            color: feedback.type === "success" ? "#166534" : "#991b1b",
            padding: "12px 18px",
            borderRadius: "10px",
            marginBottom: "18px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "13.5px",
            fontWeight: "700"
          }}
        >
          <span>{feedback.msg}</span>
          <button type="button" onClick={() => setFeedback({ type: "", msg: "" })} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: "16px" }}>✕</button>
        </div>
      )}

      {/* KPI Cards Row */}
      <div className="row g-3 mb-4">
        <div className="col-md-3 col-6">
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#64748b", textTransform: "uppercase" }}>Pending Pickups</span>
              <span style={{ fontSize: "20px" }}>⏳</span>
            </div>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#0f172a", marginTop: "6px" }}>
              {overview.pendingPickupRequests}
            </div>
            <span style={{ fontSize: "11.5px", color: "#64748b" }}>Awaiting Admin approval</span>
          </div>
        </div>

        <div className="col-md-3 col-6">
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#166534", textTransform: "uppercase" }}>Ready for Pickup</span>
              <span style={{ fontSize: "20px" }}>🏪</span>
            </div>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#16a34a", marginTop: "6px" }}>
              {overview.readyForPickup}
            </div>
            <span style={{ fontSize: "11.5px", color: "#166534" }}>Customer counter pickups</span>
          </div>
        </div>

        <div className="col-md-3 col-6">
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#1e40af", textTransform: "uppercase" }}>Vendor Deliveries</span>
              <span style={{ fontSize: "20px" }}>🛵</span>
            </div>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#2563eb", marginTop: "6px" }}>
              {overview.vendorDeliveries}
            </div>
            <span style={{ fontSize: "11.5px", color: "#1e40af" }}>Active seller deliveries</span>
          </div>
        </div>

        <div className="col-md-3 col-6">
          <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", padding: "16px", boxShadow: "0 2px 8px rgba(0,0,0,0.02)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#7e22ce", textTransform: "uppercase" }}>Pickup Locations</span>
              <span style={{ fontSize: "20px" }}>📍</span>
            </div>
            <div style={{ fontSize: "22px", fontWeight: "900", color: "#7e22ce", marginTop: "6px" }}>
              {overview.activePickupLocations}
            </div>
            <span style={{ fontSize: "11.5px", color: "#7e22ce" }}>Approved &amp; active hubs</span>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div style={{ display: "flex", gap: "8px", borderBottom: "1.5px solid #e2e8f0", paddingBottom: "10px", marginBottom: "22px", overflowX: "auto" }}>
        {[
          { id: "overview", label: "Overview", icon: "📊" },
          { id: "pickup_requests", label: "Order Pickup Requests", icon: "🚚", badge: overview.pendingPickupRequests },
          { id: "customer_pickup", label: "Customer Self Pickup", icon: "🏪", badge: overview.readyForPickup },
          { id: "vendor_delivery", label: "Delivery by Vendor", icon: "🛵", badge: overview.vendorDeliveries },
          { id: "pickup_locations", label: "Pickup Locations", icon: "📍", count: locations.length }
        ].map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "8px 16px",
              fontSize: "13px",
              fontWeight: 800,
              borderRadius: "8px",
              border: "none",
              background: activeTab === tab.id ? "#faedea" : "transparent",
              color: activeTab === tab.id ? "var(--primary)" : "#475569",
              cursor: "pointer",
              whiteSpace: "nowrap"
            }}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
            {tab.badge !== undefined && tab.badge > 0 && (
              <span style={{ background: "var(--primary)", color: "#ffffff", padding: "1px 6px", borderRadius: "99px", fontSize: "11px" }}>
                {tab.badge}
              </span>
            )}
            {tab.count !== undefined && (
              <span style={{ background: "#e2e8f0", color: "#475569", padding: "1px 6px", borderRadius: "99px", fontSize: "11px" }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── TAB 1: OVERVIEW ── */}
      {activeTab === "overview" && (
        <div className="row g-4">
          <div className="col-lg-8">
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "22px 24px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h3 style={{ fontSize: "17px", fontWeight: "900", color: "#0f172a", margin: 0 }}>
                  Recent Pickup Requests &amp; Orders
                </h3>
                <button type="button" onClick={() => setActiveTab("pickup_requests")} style={{ fontSize: "12.5px", fontWeight: "800", color: "var(--primary)", background: "none", border: "none", cursor: "pointer" }}>
                  View All &rarr;
                </button>
              </div>

              {pickupRequests.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                  {pickupRequests.slice(0, 5).map((req) => (
                    <div key={req._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 14px", background: "#f8fafc", border: "1px solid #edf0f4", borderRadius: "10px", flexWrap: "wrap", gap: "10px" }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                          <strong style={{ fontSize: "14px", color: "#0f172a" }}>#{req.orderNumber}</strong>
                          <span style={{ fontSize: "11px", color: "#64748b" }}>&bull; {req.customerName}</span>
                        </div>
                        <span style={{ fontSize: "12px", color: "#64748b", marginTop: "2px", display: "block" }}>
                          📍 Pickup from: <strong>{req.pickupLocationSnapshot?.name || "Main Store"}</strong> ({req.pickupLocationSnapshot?.city})
                        </span>
                      </div>

                      <div style={{ textAlign: "right" }}>
                        <span
                          style={{
                            fontSize: "11.5px",
                            fontWeight: 800,
                            padding: "3px 10px",
                            borderRadius: "99px",
                            background: req.status === "APPROVED" ? "#dcfce7" : req.status === "REJECTED" ? "#fee2e2" : "#fef3c7",
                            color: req.status === "APPROVED" ? "#166534" : req.status === "REJECTED" ? "#991b1b" : "#92400e",
                            border: `1px solid ${req.status === "APPROVED" ? "#bbf7d0" : req.status === "REJECTED" ? "#fecaca" : "#fde68a"}`
                          }}
                        >
                          {req.status === "APPROVED" ? "✓ Pickup Approved" : req.status === "REJECTED" ? "✕ Pickup Rejected" : "● Pending Admin Approval"}
                        </span>
                        {req.status === "REJECTED" && req.rejectionReason && (
                          <div style={{ fontSize: "11px", color: "#dc2626", marginTop: "4px" }}>
                            Reason: {req.rejectionReason}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ padding: "30px", textAlign: "center", color: "var(--text-muted)", fontSize: "13.5px" }}>
                  No pickup requests submitted yet. Select orders below to request courier/admin pickup.
                </div>
              )}
            </div>
          </div>

          <div className="col-lg-4">
            <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "22px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
              <h3 style={{ fontSize: "16px", fontWeight: "900", color: "#0f172a", marginBottom: "12px" }}>
                Fulfillment Channels &amp; Rules
              </h3>

              <div style={{ display: "flex", flexDirection: "column", gap: "12px", fontSize: "13px" }}>
                <div style={{ padding: "10px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #edf0f4" }}>
                  <strong style={{ color: "#0f172a", display: "block" }}>1. Order Pickup Requests</strong>
                  <span style={{ color: "#64748b", fontSize: "12px" }}>Request collection for packed items at your approved warehouse or store.</span>
                </div>
                <div style={{ padding: "10px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #edf0f4" }}>
                  <strong style={{ color: "#0f172a", display: "block" }}>2. Delivery by Vendor</strong>
                  <span style={{ color: "#64748b", fontSize: "12px" }}>Direct seller-to-doorstep delivery for local customers.</span>
                </div>
                <div style={{ padding: "10px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #edf0f4" }}>
                  <strong style={{ color: "#0f172a", display: "block" }}>3. Customer Self Pickup</strong>
                  <span style={{ color: "#64748b", fontSize: "12px" }}>Shoppers visit your physical store counter to collect ordered products.</span>
                </div>
                <div style={{ padding: "10px", background: "#f8fafc", borderRadius: "8px", border: "1px solid #edf0f4" }}>
                  <strong style={{ color: "#0f172a", display: "block" }}>4. Multi-Location Pickup</strong>
                  <span style={{ color: "#64748b", fontSize: "12px" }}>Add secondary stores or warehouses. Requires Admin approval.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 2: ORDER PICKUP REQUESTS ── */}
      {activeTab === "pickup_requests" && (
        <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "24px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "18px", flexWrap: "wrap", gap: "10px" }}>
            <div>
              <h3 style={{ fontSize: "18px", fontWeight: "900", color: "#0f172a", margin: 0 }}>
                Orders Ready for Pickup Request
              </h3>
              <p style={{ fontSize: "13px", color: "var(--text-muted)", margin: "2px 0 0" }}>
                Select an order to submit an Admin pickup request from your store or warehouse.
              </p>
            </div>
          </div>

          {/* Orders Table */}
          <div style={{ overflowX: "auto", marginBottom: "26px" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead>
                <tr style={{ borderBottom: "1.5px solid #edf0f4", color: "#64748b", fontSize: "11px", fontWeight: "800", textTransform: "uppercase" }}>
                  <th style={{ padding: "10px 8px", textAlign: "left" }}>ORDER #</th>
                  <th style={{ padding: "10px 8px", textAlign: "left" }}>CUSTOMER</th>
                  <th style={{ padding: "10px 8px", textAlign: "center" }}>ITEMS</th>
                  <th style={{ padding: "10px 8px", textAlign: "right" }}>VALUE</th>
                  <th style={{ padding: "10px 8px", textAlign: "left" }}>FULFILLMENT STATUS</th>
                  <th style={{ padding: "10px 8px", textAlign: "right" }}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {orders.length > 0 ? (
                  orders.map((ord) => (
                    <tr key={ord._id} style={{ borderBottom: "1px solid #edf0f4" }}>
                      <td style={{ padding: "12px 8px", fontWeight: "800", color: "#0f172a" }}>
                        #{ord.orderNumber}
                        <span style={{ display: "block", fontSize: "11px", color: "#64748b", fontWeight: "normal" }}>
                          {new Date(ord.createdAt).toLocaleDateString("en-IN", { month: "short", day: "numeric" })}
                        </span>
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <strong style={{ color: "#0f172a" }}>{ord.customerName}</strong>
                        <span style={{ display: "block", fontSize: "11.5px", color: "#64748b" }}>{ord.shippingAddress?.city}</span>
                      </td>
                      <td style={{ padding: "12px 8px", textAlign: "center", fontWeight: "700" }}>{ord.itemsCount}</td>
                      <td style={{ padding: "12px 8px", textAlign: "right", fontWeight: "800", color: "#0f172a" }}>
                        {inrFormatter.format(ord.vendorValue)}
                      </td>
                      <td style={{ padding: "12px 8px" }}>
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 800,
                            padding: "3px 8px",
                            borderRadius: "6px",
                            background: ord.fulfillmentStatus === "PICKUP_APPROVED" ? "#dcfce7" : ord.fulfillmentStatus === "PICKUP_REQUESTED" ? "#fef3c7" : "#f1f5f9",
                            color: ord.fulfillmentStatus === "PICKUP_APPROVED" ? "#166534" : ord.fulfillmentStatus === "PICKUP_REQUESTED" ? "#92400e" : "#475569"
                          }}
                        >
                          ● {ord.fulfillmentStatus}
                        </span>
                      </td>
                      <td style={{ padding: "12px 8px", textAlign: "right" }}>
                        {ord.fulfillmentStatus === "PICKUP_REQUESTED" ? (
                          <span style={{ fontSize: "12px", color: "#b45309", fontWeight: "700" }}>⏳ Request Pending</span>
                        ) : ord.fulfillmentStatus === "PICKUP_APPROVED" ? (
                          <span style={{ fontSize: "12px", color: "#166534", fontWeight: "700" }}>✓ Approved</span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedOrderForPickup(ord);
                              setSelectedLocationId(approvedLocations[0]?._id || "");
                              setPickupModalOpen(true);
                            }}
                            className="btn btn-sm btn-primary"
                            style={{ fontSize: "12px", padding: "6px 14px", fontWeight: "800" }}
                          >
                            Request Pickup
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: "center", padding: "30px", color: "var(--text-muted)" }}>
                      No orders currently awaiting pickup.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Submitted Pickup Requests History */}
          <h4 style={{ fontSize: "16px", fontWeight: "900", color: "#0f172a", marginBottom: "14px" }}>
            Submitted Pickup Requests Log
          </h4>
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            {pickupRequests.map((req) => (
              <div key={req._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 16px", background: "#f8fafc", border: "1px solid #edf0f4", borderRadius: "10px", flexWrap: "wrap", gap: "12px" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ fontSize: "11px", fontWeight: "800", background: "#e2e8f0", color: "#334155", padding: "2px 6px", borderRadius: "4px" }}>
                      {req.pickupRequestId}
                    </span>
                    <strong style={{ fontSize: "14px", color: "#0f172a" }}>Order #{req.orderNumber}</strong>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>&bull; Customer: {req.customerName}</span>
                  </div>
                  <span style={{ fontSize: "12.5px", color: "#475569", marginTop: "4px", display: "block" }}>
                    📍 Pickup Location: <strong>{req.pickupLocationSnapshot?.name || "Main Store"}</strong> ({req.pickupLocationSnapshot?.street}, {req.pickupLocationSnapshot?.city})
                  </span>
                </div>

                <div style={{ textAlign: "right" }}>
                  <span
                    style={{
                      fontSize: "12px",
                      fontWeight: 800,
                      padding: "4px 12px",
                      borderRadius: "99px",
                      background: req.status === "APPROVED" ? "#dcfce7" : req.status === "REJECTED" ? "#fee2e2" : "#fef3c7",
                      color: req.status === "APPROVED" ? "#166534" : req.status === "REJECTED" ? "#991b1b" : "#92400e",
                      border: `1px solid ${req.status === "APPROVED" ? "#bbf7d0" : req.status === "REJECTED" ? "#fecaca" : "#fde68a"}`
                    }}
                  >
                    {req.status === "APPROVED" ? "✓ Pickup Approved" : req.status === "REJECTED" ? "✕ Pickup Rejected" : "● Pending Admin Approval"}
                  </span>
                  {req.status === "REJECTED" && (
                    <div style={{ marginTop: "6px" }}>
                      <span style={{ fontSize: "11.5px", color: "#dc2626", display: "block", fontWeight: "600" }}>
                        Reason: {req.rejectionReason || "Location unavailable"}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedOrderForPickup({ _id: req.order, orderNumber: req.orderNumber, shippingInfo: { name: req.customerName } });
                          setSelectedLocationId(approvedLocations[0]?._id || "");
                          setPickupModalOpen(true);
                        }}
                        style={{ marginTop: "4px", fontSize: "11.5px", fontWeight: "800", color: "var(--primary)", background: "none", border: "none", cursor: "pointer", textDecoration: "underline" }}
                      >
                        [ Request Again ]
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── TAB 3: CUSTOMER SELF PICKUP ── */}
      {activeTab === "customer_pickup" && (
        <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "24px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
          <h3 style={{ fontSize: "18px", fontWeight: "900", color: "#0f172a", marginBottom: "4px" }}>
            Customer Counter Pickups 🏪
          </h3>
          <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "20px" }}>
            Orders placed for customer collection at your physical store. Prepare items and mark ready for pickup.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {orders.filter(o => ["CUSTOMER_PICKUP", "STORE_PICKUP", "BOOKING"].includes(o.fulfillmentMethod)).length > 0 ? (
              orders.filter(o => ["CUSTOMER_PICKUP", "STORE_PICKUP", "BOOKING"].includes(o.fulfillmentMethod)).map((ord) => (
                <div key={ord._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", background: "#f8fafc", border: "1px solid #edf0f4", borderRadius: "12px", flexWrap: "wrap", gap: "14px" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <strong style={{ fontSize: "15px", color: "#0f172a" }}>Order #{ord.orderNumber}</strong>
                      <span style={{ fontSize: "11px", fontWeight: "800", background: "#dbeafe", color: "#1d4ed8", padding: "2px 8px", borderRadius: "99px" }}>
                        CUSTOMER PICKUP
                      </span>
                    </div>
                    <div style={{ fontSize: "13px", color: "#334155", marginTop: "4px" }}>
                      Customer: <strong>{ord.customerName}</strong> &bull; 📞 {ord.customerPhone}
                    </div>
                    <span style={{ fontSize: "12px", color: "#64748b", marginTop: "2px", display: "block" }}>
                      Pickup Store: <strong>{ord.pickupLocation?.name || "Main Store"}</strong>
                    </span>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "12px", fontWeight: "800", color: ord.fulfillmentStatus === "READY_FOR_PICKUP" ? "#16a34a" : "#b45309", marginBottom: "8px" }}>
                      Status: {ord.fulfillmentStatus === "READY_FOR_PICKUP" ? "🟢 Ready for Customer" : "● Preparation in Progress"}
                    </div>

                    {ord.fulfillmentStatus === "CONFIRMED" && (
                      <button
                        type="button"
                        onClick={() => handleUpdateOrderStatus(ord._id, "READY_FOR_PICKUP")}
                        disabled={actionLoading}
                        className="btn btn-sm btn-primary"
                        style={{ fontSize: "12px", padding: "6px 14px", fontWeight: "800" }}
                      >
                        [ Mark Ready for Pickup ]
                      </button>
                    )}

                    {ord.fulfillmentStatus === "READY_FOR_PICKUP" && (
                      <button
                        type="button"
                        onClick={() => handleUpdateOrderStatus(ord._id, "PICKED_UP")}
                        disabled={actionLoading}
                        className="btn btn-sm"
                        style={{ fontSize: "12px", padding: "6px 14px", fontWeight: "800", background: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" }}
                      >
                        ✓ Confirm Customer Handover
                      </button>
                    )}

                    {ord.fulfillmentStatus === "PICKED_UP" && (
                      <span style={{ fontSize: "12.5px", fontWeight: "800", color: "#16a34a" }}>✓ Handover Completed</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: "36px", textAlign: "center", color: "var(--text-muted)", fontSize: "13.5px" }}>
                No active customer counter pickups at this time.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB 4: DELIVERY BY VENDOR ── */}
      {activeTab === "vendor_delivery" && (
        <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "24px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
          <h3 style={{ fontSize: "18px", fontWeight: "900", color: "#0f172a", marginBottom: "4px" }}>
            Delivery by Vendor 🛵
          </h3>
          <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "20px" }}>
            Direct deliveries handled by your store's in-house staff or riders. Update delivery milestones accurately.
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {orders.filter(o => o.fulfillmentMethod === "VENDOR_DELIVERY").length > 0 ? (
              orders.filter(o => o.fulfillmentMethod === "VENDOR_DELIVERY").map((ord) => (
                <div key={ord._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", background: "#f8fafc", border: "1px solid #edf0f4", borderRadius: "12px", flexWrap: "wrap", gap: "14px" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <strong style={{ fontSize: "15px", color: "#0f172a" }}>Order #{ord.orderNumber}</strong>
                      <span style={{ fontSize: "11px", fontWeight: "800", background: "#e0e7ff", color: "#3730a3", padding: "2px 8px", borderRadius: "99px" }}>
                        VENDOR DIRECT
                      </span>
                    </div>
                    <div style={{ fontSize: "13px", color: "#334155", marginTop: "4px" }}>
                      Customer: <strong>{ord.customerName}</strong> ({ord.shippingAddress?.phoneNo})
                    </div>
                    <span style={{ fontSize: "12px", color: "#64748b", marginTop: "2px", display: "block" }}>
                      🏠 Delivery Address: {ord.shippingAddress?.address}, {ord.shippingAddress?.city} ({ord.shippingAddress?.pincode})
                    </span>
                  </div>

                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: "12px", fontWeight: "800", color: "#1e40af", marginBottom: "8px" }}>
                      Milestone: {ord.fulfillmentStatus}
                    </div>

                    {ord.fulfillmentStatus === "CONFIRMED" && (
                      <button
                        type="button"
                        onClick={() => handleUpdateOrderStatus(ord._id, "READY_FOR_DISPATCH")}
                        disabled={actionLoading}
                        className="btn btn-sm btn-primary"
                        style={{ fontSize: "12px", padding: "6px 14px", fontWeight: "800" }}
                      >
                        [ Ready for Dispatch ]
                      </button>
                    )}

                    {ord.fulfillmentStatus === "READY_FOR_DISPATCH" && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedOrderForDelivery(ord);
                          setDeliveryModalOpen(true);
                        }}
                        disabled={actionLoading}
                        className="btn btn-sm"
                        style={{ fontSize: "12px", padding: "6px 14px", fontWeight: "800", background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe" }}
                      >
                        🛵 Dispatch / Out for Delivery
                      </button>
                    )}

                    {ord.fulfillmentStatus === "OUT_FOR_DELIVERY" && (
                      <button
                        type="button"
                        onClick={() => handleUpdateOrderStatus(ord._id, "DELIVERED")}
                        disabled={actionLoading}
                        className="btn btn-sm"
                        style={{ fontSize: "12px", padding: "6px 14px", fontWeight: "800", background: "#dcfce7", color: "#166534", border: "1px solid #bbf7d0" }}
                      >
                        ✓ Mark Order Delivered
                      </button>
                    )}

                    {ord.fulfillmentStatus === "DELIVERED" && (
                      <span style={{ fontSize: "12.5px", fontWeight: "800", color: "#16a34a" }}>✓ Delivered to Customer</span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: "36px", textAlign: "center", color: "var(--text-muted)", fontSize: "13.5px" }}>
                No active seller direct deliveries currently in transit.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── TAB 5: PICKUP LOCATIONS ── */}
      {activeTab === "pickup_locations" && (
        <div style={{ background: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "14px", padding: "24px", boxShadow: "0 2px 10px rgba(0,0,0,0.02)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "10px" }}>
            <div>
              <h3 style={{ fontSize: "18px", fontWeight: "900", color: "#0f172a", margin: 0 }}>
                Store &amp; Warehouse Pickup Locations 📍
              </h3>
              <p style={{ fontSize: "13px", color: "var(--text-muted)", margin: "2px 0 0" }}>
                Approved locations are available for courier pickups and customer collections. New locations require Admin approval.
              </p>
            </div>

            <button
              type="button"
              onClick={() => setLocationModalOpen(true)}
              className="btn btn-primary"
              style={{ padding: "8px 16px", fontSize: "13px", fontWeight: "800" }}
            >
              + Add Pickup Location
            </button>
          </div>

          <div className="row g-3">
            {locations.map((loc) => (
              <div key={loc._id} className="col-md-6">
                <div style={{ background: "#f8fafc", border: "1.5px solid #edf0f4", borderRadius: "12px", padding: "18px", height: "100%", position: "relative" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "8px" }}>
                    <div>
                      <span style={{ fontSize: "15px", fontWeight: "900", color: "#0f172a" }}>{loc.name}</span>
                      {loc.isDefault && (
                        <span style={{ marginLeft: "8px", fontSize: "10px", fontWeight: "800", background: "#f1f5f9", color: "#475569", padding: "2px 6px", borderRadius: "4px" }}>
                          PRIMARY
                        </span>
                      )}
                    </div>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 800,
                        padding: "3px 8px",
                        borderRadius: "99px",
                        background: loc.status === "APPROVED" ? (loc.isActive ? "#dcfce7" : "#f1f5f9") : loc.status === "REJECTED" ? "#fee2e2" : "#fef3c7",
                        color: loc.status === "APPROVED" ? (loc.isActive ? "#166534" : "#64748b") : loc.status === "REJECTED" ? "#991b1b" : "#92400e",
                        border: `1px solid ${loc.status === "APPROVED" ? (loc.isActive ? "#bbf7d0" : "#cbd5e1") : loc.status === "REJECTED" ? "#fecaca" : "#fde68a"}`
                      }}
                    >
                      {loc.status === "APPROVED" ? (loc.isActive ? "✓ Approved" : "🔒 Disabled by Admin") : loc.status === "REJECTED" ? "✕ Rejected" : "● Pending Admin Approval"}
                    </span>
                  </div>

                  <p style={{ fontSize: "13px", color: "#334155", margin: "0 0 8px" }}>
                    📍 {loc.address?.street}{loc.address?.addressLine2 ? `, ${loc.address.addressLine2}` : ""}, {loc.address?.city}, {loc.address?.state} - {loc.address?.pincode}
                  </p>

                  <div style={{ fontSize: "12px", color: "#64748b", borderTop: "1px solid #edf0f4", paddingTop: "8px", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: "6px" }}>
                    <span>👤 Contact: <strong>{loc.contactPerson}</strong> ({loc.contactPhone})</span>
                    <span>🕒 {loc.operatingHours}</span>
                  </div>

                  {loc.status === "REJECTED" && loc.rejectionReason && (
                    <div style={{ marginTop: "8px", padding: "6px 10px", background: "#fef2f2", borderRadius: "6px", fontSize: "11.5px", color: "#dc2626", fontWeight: "600" }}>
                      Rejection Reason: {loc.rejectionReason}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ═══════════════════════ MODAL 1: REQUEST PICKUP ═══════════════════════ */}
      {pickupModalOpen && selectedOrderForPickup && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 9999, padding: "20px" }}>
          <div style={{ background: "#ffffff", borderRadius: "16px", padding: "26px", maxWidth: "520px", width: "100%", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "900", color: "#0f172a", marginBottom: "4px" }}>
              Request Order Pickup
            </h3>
            <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "18px" }}>
              Order #{selectedOrderForPickup.orderNumber} &bull; Customer: {selectedOrderForPickup.customerName || selectedOrderForPickup.shippingInfo?.name}
            </p>

            <form onSubmit={handleCreatePickupRequest}>
              <div style={{ marginBottom: "16px" }}>
                <label style={{ fontSize: "12.5px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "6px" }}>
                  Select Pickup Location *
                </label>
                <select
                  value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}
                  required
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px", fontWeight: "700" }}
                >
                  {approvedLocations.map((l) => (
                    <option key={l._id} value={l._id}>
                      {l.name} — {l.address?.street}, {l.address?.city}
                    </option>
                  ))}
                </select>
                {approvedLocations.length === 0 && (
                  <span style={{ fontSize: "11.5px", color: "#dc2626", marginTop: "4px", display: "block" }}>
                    No approved pickup locations available. Please add a location first.
                  </span>
                )}
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ fontSize: "12.5px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "6px" }}>
                  Package Notes / Special Instructions
                </label>
                <textarea
                  rows="2"
                  value={pickupNotes}
                  onChange={(e) => setPickupNotes(e.target.value)}
                  placeholder="e.g., Packed in box #2, fragile shoes"
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px" }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  onClick={() => setPickupModalOpen(false)}
                  className="btn btn-outline-secondary"
                  style={{ padding: "8px 16px", fontSize: "13px", fontWeight: "800" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading || approvedLocations.length === 0}
                  className="btn btn-primary"
                  style={{ padding: "8px 20px", fontSize: "13px", fontWeight: "800" }}
                >
                  {actionLoading ? "Submitting..." : "Submit Pickup Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════ MODAL 2: REQUEST NEW PICKUP LOCATION ═══════════════════════ */}
      {locationModalOpen && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 9999, padding: "20px", overflowY: "auto" }}>
          <div style={{ background: "#ffffff", borderRadius: "16px", padding: "26px", maxWidth: "600px", width: "100%", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)", maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "900", color: "#0f172a", marginBottom: "4px" }}>
              Request New Pickup Location
            </h3>
            <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "18px" }}>
              Submit a secondary store, warehouse, or distribution hub. It will be enabled after Admin review.
            </p>

            <form onSubmit={handleCreateLocationRequest}>
              <div className="row g-3">
                <div className="col-12">
                  <label style={{ fontSize: "12px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "4px" }}>Location Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., Central Warehouse, Secondary Store"
                    value={newLocationForm.name}
                    onChange={(e) => setNewLocationForm({ ...newLocationForm, name: e.target.value })}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-12">
                  <label style={{ fontSize: "12px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "4px" }}>Street Address *</label>
                  <input
                    type="text"
                    required
                    placeholder="Building, Street, Industrial Area"
                    value={newLocationForm.street}
                    onChange={(e) => setNewLocationForm({ ...newLocationForm, street: e.target.value })}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-6">
                  <label style={{ fontSize: "12px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "4px" }}>City *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., Delhi, Mumbai"
                    value={newLocationForm.city}
                    onChange={(e) => setNewLocationForm({ ...newLocationForm, city: e.target.value })}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-6">
                  <label style={{ fontSize: "12px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "4px" }}>State *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., Delhi, Maharashtra"
                    value={newLocationForm.state}
                    onChange={(e) => setNewLocationForm({ ...newLocationForm, state: e.target.value })}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-6">
                  <label style={{ fontSize: "12px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "4px" }}>PIN Code *</label>
                  <input
                    type="text"
                    required
                    placeholder="6-digit PIN code"
                    value={newLocationForm.pincode}
                    onChange={(e) => setNewLocationForm({ ...newLocationForm, pincode: e.target.value })}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-6">
                  <label style={{ fontSize: "12px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "4px" }}>Landmark</label>
                  <input
                    type="text"
                    placeholder="Nearby landmark (optional)"
                    value={newLocationForm.landmark}
                    onChange={(e) => setNewLocationForm({ ...newLocationForm, landmark: e.target.value })}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-6">
                  <label style={{ fontSize: "12px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "4px" }}>Contact Person *</label>
                  <input
                    type="text"
                    required
                    placeholder="Manager / Dispatcher Name"
                    value={newLocationForm.contactPerson}
                    onChange={(e) => setNewLocationForm({ ...newLocationForm, contactPerson: e.target.value })}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-md-6">
                  <label style={{ fontSize: "12px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "4px" }}>Contact Phone *</label>
                  <input
                    type="text"
                    required
                    placeholder="10-digit mobile number"
                    value={newLocationForm.contactPhone}
                    onChange={(e) => setNewLocationForm({ ...newLocationForm, contactPhone: e.target.value })}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-12">
                  <label style={{ fontSize: "12px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "4px" }}>Operating Hours</label>
                  <input
                    type="text"
                    placeholder="e.g., Mon - Sat: 9:00 AM - 7:00 PM"
                    value={newLocationForm.operatingHours}
                    onChange={(e) => setNewLocationForm({ ...newLocationForm, operatingHours: e.target.value })}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px" }}
                  />
                </div>

                <div className="col-12">
                  <label style={{ fontSize: "12px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "4px" }}>Reason for Adding Location</label>
                  <textarea
                    rows="2"
                    placeholder="Briefly state why this warehouse or store is needed..."
                    value={newLocationForm.reason}
                    onChange={(e) => setNewLocationForm({ ...newLocationForm, reason: e.target.value })}
                    style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px" }}
                  />
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", marginTop: "20px" }}>
                <button
                  type="button"
                  onClick={() => setLocationModalOpen(false)}
                  className="btn btn-outline-secondary"
                  style={{ padding: "8px 16px", fontSize: "13px", fontWeight: "800" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="btn btn-primary"
                  style={{ padding: "8px 20px", fontSize: "13px", fontWeight: "800" }}
                >
                  {actionLoading ? "Submitting..." : "Submit Location Request"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══════════════════════ MODAL 3: VENDOR DELIVERY DISPATCH ═══════════════════════ */}
      {deliveryModalOpen && selectedOrderForDelivery && (
        <div style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100%", background: "rgba(0,0,0,0.5)", display: "grid", placeItems: "center", zIndex: 9999, padding: "20px" }}>
          <div style={{ background: "#ffffff", borderRadius: "16px", padding: "26px", maxWidth: "500px", width: "100%", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.1)" }}>
            <h3 style={{ fontSize: "18px", fontWeight: "900", color: "#0f172a", marginBottom: "4px" }}>
              Dispatch Vendor Delivery
            </h3>
            <p style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "16px" }}>
              Order #{selectedOrderForDelivery.orderNumber} &bull; {selectedOrderForDelivery.customerName}
            </p>

            <form onSubmit={(e) => {
              e.preventDefault();
              handleUpdateOrderStatus(selectedOrderForDelivery._id, "OUT_FOR_DELIVERY", deliveryForm);
            }}>
              <div style={{ marginBottom: "12px" }}>
                <label style={{ fontSize: "12px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "4px" }}>Delivery Agent Name</label>
                <input
                  type="text"
                  placeholder="e.g., Ramesh Kumar"
                  value={deliveryForm.deliveryPersonName}
                  onChange={(e) => setDeliveryForm({ ...deliveryForm, deliveryPersonName: e.target.value })}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px" }}
                />
              </div>

              <div style={{ marginBottom: "12px" }}>
                <label style={{ fontSize: "12px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "4px" }}>Agent Phone Number</label>
                <input
                  type="text"
                  placeholder="10-digit phone number"
                  value={deliveryForm.deliveryPersonPhone}
                  onChange={(e) => setDeliveryForm({ ...deliveryForm, deliveryPersonPhone: e.target.value })}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px" }}
                />
              </div>

              <div style={{ marginBottom: "20px" }}>
                <label style={{ fontSize: "12px", fontWeight: "800", color: "#334155", display: "block", marginBottom: "4px" }}>Vehicle Number / Note</label>
                <input
                  type="text"
                  placeholder="e.g., DL 1S AB 1234 (optional)"
                  value={deliveryForm.vehicleNumber}
                  onChange={(e) => setDeliveryForm({ ...deliveryForm, vehicleNumber: e.target.value })}
                  style={{ width: "100%", padding: "9px 12px", borderRadius: "8px", border: "1.5px solid #cbd5e1", fontSize: "13.5px" }}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
                <button
                  type="button"
                  onClick={() => setDeliveryModalOpen(false)}
                  className="btn btn-outline-secondary"
                  style={{ padding: "8px 16px", fontSize: "13px", fontWeight: "800" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={actionLoading}
                  className="btn btn-primary"
                  style={{ padding: "8px 20px", fontSize: "13px", fontWeight: "800" }}
                >
                  {actionLoading ? "Dispatching..." : "Confirm Dispatch & Out for Delivery"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
